import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import type { GlobalSearchFetchRecentSuccessPayload } from '@/features/globalSearch/store/types/GlobalSearchFetchRecentSuccessPayload';
import type { GlobalSearchInitialState } from '@/features/globalSearch/store/types/GlobalSearchInitialState';
import type { GlobalSearchSearchPayload } from '@/features/globalSearch/store/types/GlobalSearchSearchPayload';
import type { GlobalSearchSearchSuccessPayload } from '@/features/globalSearch/store/types/GlobalSearchSearchSuccessPayload';

const initialState: GlobalSearchInitialState = {
  recentList: [],
  list: [],
  statuses: {
    isLoading: false,
    isError: false,
    isSuccess: false,
  },
  recentStatuses: {
    isRecentRetrieved: false,
    isRecentError: false,
    isLoading: false,
  },
  pagination: {
    numberOfItems: 0,
    first: '1',
    last: undefined,
    previous: '0',
    next: '0',
    page: 1,
    perPage: 3,
    isLastPage: true,
  },
};

const globalSearchSlice = createSlice({
  name: 'globalSearchSlice',
  initialState,
  reducers: {
    search: (state, _payload: PayloadAction<GlobalSearchSearchPayload>) => {
      state.list = [];
      state.statuses = { ...initialState.statuses, isLoading: true };
      state.pagination.page = 1;
    },
    searchSuccess: (state, { payload }: PayloadAction<GlobalSearchSearchSuccessPayload>) => {
      state.statuses = { ...initialState.statuses, isSuccess: true };
      state.list = payload.shouldRewrite ? payload.list : [...state.list, ...payload.list];
      state.pagination = {
        ...payload.pagination,
        isLastPage: payload.pagination.page * payload.pagination.perPage >= payload.pagination.numberOfItems,
      };
    },
    searchFailed: (state) => {
      state.statuses = { ...initialState.statuses, isError: true };
      state.list = [];
      state.pagination = initialState.pagination;
    },
    resetSearch: (state) => {
      state.list = [];
      state.statuses = initialState.statuses;
      state.pagination = initialState.pagination;
    },
    incrementSearchPage: (state, _payload: PayloadAction<GlobalSearchSearchPayload>) => {
      state.statuses = { ...initialState.statuses, isLoading: true };
      state.pagination.page = state.pagination.page + 1;
    },
    setRecentListLoading: (state, { payload }: PayloadAction<boolean>) => {
      state.recentStatuses.isLoading = payload;
    },
    fetchRecentList: () => {},
    fetchRecentListSuccess: (state, { payload }: PayloadAction<GlobalSearchFetchRecentSuccessPayload>) => {
      state.recentStatuses.isRecentRetrieved = true;
      state.recentList = payload.list;
    },
    fetchRecentListFailed: (state) => {
      state.recentStatuses.isRecentError = true;
      state.recentList = [];
    },
  },
});

export const {
  search,
  searchSuccess,
  searchFailed,
  resetSearch,
  incrementSearchPage,
  fetchRecentList,
  fetchRecentListSuccess,
  fetchRecentListFailed,
  setRecentListLoading,
} = globalSearchSlice.actions;

export const globalSearchReducer = globalSearchSlice.reducer;

export const globalSearchReducerPath = globalSearchSlice.reducerPath;
